/**
 * 跨路由的全局音乐播放器。
 *
 * Provider 挂在 RouterProvider 外：切聊天模式、打开回忆或进入“我的”只替换路由
 * 内容，不会卸载唯一的 audio 元素。清唱片段不进入这里，由消息内 audio 控件播放。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { MusicPart } from "@dreamverse/contracts";
import { useSession } from "../state/session";
import { IconClose, IconMusic, IconPause, IconPlay } from "./Icons";
import { initialMusicVolume, persistMusicVolume } from "./music-volume";

export interface PlayerTrack {
  recordingId: string;
  title: string;
  artist?: string;
  versionName?: string;
  coverAssetId?: string;
  durationMs?: number;
  audioMode?: "mixed" | "vocal_only";
}

interface MusicPlayerValue {
  supported: boolean;
  track: PlayerTrack | null;
  isLoading: boolean;
  isPlaying: boolean;
  panelOpen: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  error: string | null;
  playTrack: (track: PlayerTrack) => void;
  pause: () => void;
  stop: () => void;
  toggle: () => void;
  seek: (seconds: number) => void;
  setVolume: (volume: number) => void;
  openPanel: () => void;
  closePanel: () => void;
}

const MusicPlayerContext = createContext<MusicPlayerValue | null>(null);

const initialVolume = (): number => initialMusicVolume(localStorage);

export function musicTrackOf(part: MusicPart): PlayerTrack {
  return {
    recordingId: part.trackId,
    title: part.title,
    ...(part.artist ? { artist: part.artist } : {}),
    ...(part.versionName ? { versionName: part.versionName } : {}),
    ...(part.coverAssetId ? { coverAssetId: part.coverAssetId } : {}),
    ...(part.durationMs === undefined ? {} : { durationMs: part.durationMs }),
    ...(part.audioMode ? { audioMode: part.audioMode } : {}),
  };
}

export function MusicPlayerProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const supported = session.host.capabilities.audioPlayback;
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackRef = useRef<PlayerTrack | null>(null);
  const shouldAutoplayRef = useRef(false);
  const [track, setTrackState] = useState<PlayerTrack | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(initialVolume);
  const [error, setError] = useState<string | null>(null);

  const begin = useCallback(async (): Promise<void> => {
    const audio = audioRef.current;
    if (!audio || !supported) return;
    if (!audio.paused) return;
    setIsLoading(true);
    try {
      await audio.play();
      setError(null);
    } catch {
      // 异步工具响应到达时，部分浏览器已不再把它算作用户手势。
      // 保留选中的歌并亮出播放器，用户点一次播放即可继续。
      setIsLoading(false);
      setIsPlaying(false);
      setError("浏览器暂停了自动播放，点播放键继续");
      setPanelOpen(true);
    }
  }, [supported]);

  const playTrack = useCallback(
    (next: PlayerTrack): void => {
      if (!supported) return;
      if (trackRef.current?.recordingId === next.recordingId) {
        void begin();
        return;
      }
      trackRef.current = next;
      shouldAutoplayRef.current = true;
      setIsLoading(true);
      setTrackState(next);
      setCurrentTime(0);
      setDuration((next.durationMs ?? 0) / 1_000);
      setError(null);
    },
    [begin, supported],
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    audio.src = `/api/v1/app/music/recordings/${encodeURIComponent(track.recordingId)}/audio`;
    audio.load();
    if (shouldAutoplayRef.current) {
      shouldAutoplayRef.current = false;
      void begin();
    }
  }, [begin, track]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.volume = volume;
    persistMusicVolume(localStorage, volume);
  }, [volume]);

  useEffect(() => {
    if (session.status !== "anonymous" && session.status !== "blocked") return;
    const audio = audioRef.current;
    audio?.pause();
    if (audio) audio.removeAttribute("src");
    trackRef.current = null;
    setIsLoading(false);
    setTrackState(null);
    setPanelOpen(false);
  }, [session.status]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !trackRef.current) return;
    if (audio.paused) void begin();
    else audio.pause();
  }, [begin]);

  const pause = useCallback(() => audioRef.current?.pause(), []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setIsLoading(false);
    setCurrentTime(0);
  }, []);

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(seconds)) return;
    audio.currentTime = Math.max(0, Math.min(seconds, audio.duration || seconds));
    setCurrentTime(audio.currentTime);
  }, []);

  const setVolume = useCallback((next: number) => {
    setVolumeState(Math.max(0, Math.min(1, next)));
  }, []);

  const value = useMemo<MusicPlayerValue>(
    () => ({
      supported,
      track,
      isLoading,
      isPlaying,
      panelOpen,
      currentTime,
      duration,
      volume,
      error,
      playTrack,
      pause,
      stop,
      toggle,
      seek,
      setVolume,
      openPanel: () => setPanelOpen(true),
      closePanel: () => setPanelOpen(false),
    }),
    [
      currentTime,
      duration,
      error,
      isLoading,
      isPlaying,
      panelOpen,
      pause,
      playTrack,
      seek,
      setVolume,
      stop,
      supported,
      toggle,
      track,
      volume,
    ],
  );

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        preload="metadata"
        onPlaying={() => {
          setIsLoading(false);
          setIsPlaying(true);
        }}
        onWaiting={() => {
          setIsLoading(true);
          setIsPlaying(false);
        }}
        onPause={() => {
          setIsLoading(false);
          setIsPlaying(false);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const next = event.currentTarget.duration;
          if (Number.isFinite(next)) setDuration(next);
        }}
        onDurationChange={(event) => {
          const next = event.currentTarget.duration;
          if (Number.isFinite(next)) setDuration(next);
        }}
        onEnded={() => {
          setIsLoading(false);
          setIsPlaying(false);
        }}
        onError={() => {
          setIsLoading(false);
          setIsPlaying(false);
          setError("这首歌暂时没有加载出来");
          setPanelOpen(true);
        }}
      />
    </MusicPlayerContext.Provider>
  );
}

export function useMusicPlayer(): MusicPlayerValue {
  const value = useContext(MusicPlayerContext);
  if (!value) throw new Error("useMusicPlayer 必须在 MusicPlayerProvider 内使用");
  return value;
}

const timeText = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

export function MusicPlayerPopover() {
  const player = useMusicPlayer();
  if (!player.panelOpen) return null;

  return (
    <aside className="music-player" aria-label="音乐播放器">
      <button
        type="button"
        className="music-player__close"
        onClick={player.closePanel}
        aria-label="收起播放器"
      >
        <IconClose size={16} />
      </button>

      {player.track ? (
        <>
          <div className="music-player__cover">
            {player.track.coverAssetId ? (
              <img
                src={`/api/v1/app/assets/${encodeURIComponent(player.track.coverAssetId)}`}
                alt={`${player.track.title}封面`}
              />
            ) : (
              <IconMusic size={28} />
            )}
          </div>
          <div className="music-player__copy">
            <strong>{player.track.title}</strong>
            <span>
              {[player.track.artist, player.track.versionName].filter(Boolean).join(" · ")}
            </span>
          </div>
          <div className="music-player__transport">
            <button
              type="button"
              className="music-player__toggle"
              onClick={player.toggle}
              disabled={!player.supported || player.isLoading}
              aria-label={player.isLoading ? "正在加载" : player.isPlaying ? "暂停" : "播放"}
              aria-busy={player.isLoading}
            >
              {player.isLoading ? (
                <i className="music-player__loading-spinner" aria-hidden />
              ) : player.isPlaying ? (
                <IconPause size={20} />
              ) : (
                <IconPlay size={20} />
              )}
            </button>
            <label className="music-player__volume">
              <span>音量</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={player.volume}
                onChange={(event) => player.setVolume(Number(event.target.value))}
              />
            </label>
          </div>
          <label className="music-player__progress">
            <span>{timeText(player.currentTime)}</span>
            <input
              type="range"
              min={0}
              max={Math.max(player.duration, 0.01)}
              step={0.1}
              value={Math.min(player.currentTime, Math.max(player.duration, 0.01))}
              onChange={(event) => player.seek(Number(event.target.value))}
              aria-label="播放进度"
            />
            <span>{timeText(player.duration)}</span>
          </label>
          {player.error ? <p className="music-player__error">{player.error}</p> : null}
        </>
      ) : (
        <div className="music-player__empty">
          <IconMusic size={26} />
          <strong>还没有在播放</strong>
          <span>在对话里让她放首歌吧</span>
        </div>
      )}
    </aside>
  );
}
